// File: frontend/src/pages/aitests/SavedTests.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/axios';
import { useSchool } from '../../context/SchoolContext';

const SavedTests = () => {
  const navigate = useNavigate();
  const { school } = useSchool();

  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filters
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedClass, setSelectedClass] = useState('');

  // Active View Modal
  const [viewingTest, setViewingTest] = useState(null);

  const subjectList = [
    'Mathematics', 'Physics', 'Chemistry', 'Biology', 'English',
    'Urdu', 'Computer Science', 'Islamic Studies', 'Pakistan Studies'
  ];

  const classList = [
    'Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5',
    'Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10'
  ];

  const fetchSavedTests = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      let url = '/api/v1/ai-tests/saved?';
      if (selectedSubject) url += `subject=${encodeURIComponent(selectedSubject)}&`;
      if (selectedClass) url += `class_name=${encodeURIComponent(selectedClass)}&`;

      const res = await api.get(url);
      if (res.data?.success) {
        setTests(res.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching saved tests:', err);
      setError(err.response?.data?.message || 'Failed to load saved tests');
    } finally {
      setLoading(false);
    }
  }, [selectedSubject, selectedClass]);

  useEffect(() => {
    fetchSavedTests();
  }, [fetchSavedTests]);

  const handleDeleteTest = async (id) => {
    if (!window.confirm('Are you sure you want to delete this saved test?')) return;
    try {
      const res = await api.delete(`/api/v1/ai-tests/${id}`);
      if (res.data?.success) {
        setTests((prev) => prev.filter((t) => t.id !== id));
        if (viewingTest && viewingTest.id === id) setViewingTest(null);
      }
    } catch (err) {
      alert('Failed to delete test paper.');
    }
  };

  const handlePrintModal = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 p-4 sm:p-6 font-sans">
      <div className="max-w-6xl mx-auto space-y-6">

        {/* Top Header */}
        <div className="no-print bg-slate-800 border border-slate-700 rounded-xl p-5 shadow-lg flex flex-col sm:flex-row justify-between items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white tracking-wide flex items-center space-x-2">
              <span>📁</span>
              <span>Saved AI Tests & Exams</span>
            </h1>
            <p className="text-slate-400 text-xs sm:text-sm mt-1">
              View, print, and manage all test papers generated and saved by your school teachers.
            </p>
          </div>

          <button
            onClick={() => navigate('/ai-tests')}
            className="bg-blue-600 hover:bg-blue-500 text-white text-xs sm:text-sm font-semibold px-4 py-2.5 rounded-lg shadow transition flex items-center space-x-2 cursor-pointer"
          >
            <span>✨</span>
            <span>Generate New Test</span>
          </button>
        </div>

        {/* Filters */}
        <div className="no-print bg-slate-800 border border-slate-700 rounded-xl p-4 shadow flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-xs font-medium text-slate-300 mb-1">Filter by Subject</label>
            <select
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
            >
              <option value="">All Subjects</option>
              {subjectList.map((sub) => (
                <option key={sub} value={sub}>{sub}</option>
              ))}
            </select>
          </div>

          <div className="flex-1">
            <label className="block text-xs font-medium text-slate-300 mb-1">Filter by Class</label>
            <select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
            >
              <option value="">All Classes</option>
              {classList.map((cls) => (
                <option key={cls} value={cls}>{cls}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Main Table */}
        <div className="no-print bg-slate-800 border border-slate-700 rounded-xl shadow-xl overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-slate-400">Loading saved test papers...</div>
          ) : error ? (
            <div className="p-8 text-center text-rose-400">{error}</div>
          ) : tests.length === 0 ? (
            <div className="p-12 text-center text-slate-400">
              No saved tests found. Generate and save your first test paper using the AI generator!
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm text-slate-300">
                <thead className="bg-slate-900/80 text-xs uppercase tracking-wider text-slate-400 border-b border-slate-700">
                  <tr>
                    <th className="py-3.5 px-4">Title</th>
                    <th className="py-3.5 px-4">Subject</th>
                    <th className="py-3.5 px-4">Class</th>
                    <th className="py-3.5 px-4">Mode</th>
                    <th className="py-3.5 px-4">Total Marks</th>
                    <th className="py-3.5 px-4">Date</th>
                    <th className="py-3.5 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/60">
                  {tests.map((t) => (
                    <tr key={t.id} className="hover:bg-slate-700/40 transition">
                      <td className="py-3.5 px-4 font-semibold text-white">{t.title}</td>
                      <td className="py-3.5 px-4">{t.subject}</td>
                      <td className="py-3.5 px-4">{t.class_name}</td>
                      <td className="py-3.5 px-4">
                        <span className={`text-xs px-2 py-0.5 rounded font-bold uppercase ${
                          t.mode === 'content' ? 'bg-indigo-900/80 text-indigo-300 border border-indigo-700' : 'bg-blue-900/80 text-blue-300 border border-blue-700'
                        }`}>
                          {t.mode}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 font-bold text-amber-400">{t.total_marks}</td>
                      <td className="py-3.5 px-4 text-slate-400 text-xs">
                        {new Date(t.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-3.5 px-4 text-right space-x-2">
                        <button
                          onClick={() => setViewingTest(t)}
                          className="bg-blue-600 hover:bg-blue-500 text-white text-xs px-2.5 py-1.5 rounded transition"
                        >
                          View
                        </button>
                        <button
                          onClick={() => handleDeleteTest(t.id)}
                          className="bg-rose-600 hover:bg-rose-500 text-white text-xs px-2.5 py-1.5 rounded transition"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* View Modal */}
        {viewingTest && (
          <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-4 shadow-2xl">
              
              <div className="no-print flex justify-between items-center border-b border-slate-700 pb-3">
                <h3 className="text-lg font-bold text-white">{viewingTest.title}</h3>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handlePrintModal}
                    className="bg-blue-600 hover:bg-blue-500 text-white text-xs px-3 py-1.5 rounded font-semibold"
                  >
                    Print
                  </button>
                  <button
                    onClick={() => setViewingTest(null)}
                    className="text-slate-400 hover:text-white font-bold text-xl px-2"
                  >
                    &times;
                  </button>
                </div>
              </div>

              {/* Rendered Preview Card */}
              <div className="print-container bg-white text-slate-900 p-6 rounded-xl space-y-6">
                <div className="text-center border-b pb-3">
                  <h2 className="text-xl font-extrabold">{school.name}</h2>
                  <h3 className="text-md font-bold">{viewingTest.title}</h3>
                  <div className="text-xs text-slate-600 font-semibold space-x-3 mt-1">
                    <span>Subject: {viewingTest.subject}</span>
                    <span>|</span>
                    <span>Class: {viewingTest.class_name}</span>
                    <span>|</span>
                    <span>Total Marks: {viewingTest.total_marks}</span>
                  </div>
                </div>

                {viewingTest.test_content?.sections?.mcqs?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-bold border-b text-sm">Section A: MCQs</h4>
                    {viewingTest.test_content.sections.mcqs.map((q, i) => (
                      <div key={i} className="text-xs space-y-1">
                        <div>Q{i+1}. {q.question}</div>
                        {q.options && <div className="grid grid-cols-2 gap-1 text-slate-700 pl-4">{q.options.map((opt, oi) => <span key={oi}>{opt}</span>)}</div>}
                      </div>
                    ))}
                  </div>
                )}

                {viewingTest.test_content?.sections?.short?.length > 0 && (
                  <div className="space-y-2 pt-2">
                    <h4 className="font-bold border-b text-sm">Section B: Short Questions</h4>
                    {viewingTest.test_content.sections.short.map((q, i) => (
                      <div key={i} className="text-xs">Q{i+1}. {q.question} [{q.marks || 4} marks]</div>
                    ))}
                  </div>
                )}

                {viewingTest.test_content?.sections?.long?.length > 0 && (
                  <div className="space-y-2 pt-2">
                    <h4 className="font-bold border-b text-sm">Section C: Long Questions</h4>
                    {viewingTest.test_content.sections.long.map((q, i) => (
                      <div key={i} className="text-xs">Q{i+1}. {q.question} [{q.marks || 10} marks]</div>
                    ))}
                  </div>
                )}
              </div>

            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default SavedTests;
